import { isNotEmpty, isValidEmail, isValidZipCode } from '@/helpers/validators';
import { useTranslation } from '@/i18n';
import { FormField, FormFieldType } from '@/models/customizationModels';

export default function useFormFields() {
  const { t } = useTranslation('auth');

  return [
    {
      showFieldKey: 'form_first_name_checked',
      fieldPositionKey: 'form_first_name_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'first_name',
      fieldLabelKey: 'form_first_name_label',
      fieldLabelValue: t('register.form.firstName'),
      isFieldRequiredKey: 'form_first_name_required',
      fieldErrorMsgKey: 'first_name',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_last_name_checked',
      fieldPositionKey: 'form_last_name_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'last_name',
      fieldLabelKey: 'form_last_name_label',
      fieldLabelValue: t('register.form.lastName'),
      isFieldRequiredKey: 'form_last_name_required',
      fieldErrorMsgKey: 'last_name',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_email_checked',
      fieldPositionKey: 'form_email_position',
      fieldType: FormFieldType.EMAIL,
      fieldName: 'email',
      fieldLabelKey: 'form_email_label',
      fieldLabelValue: t('register.form.email'),
      isFieldRequiredKey: 'form_email_required',
      fieldErrorMsgKey: 'email',
      isEmail: true,
      validator: (value: any, isRequired: boolean) => (!isRequired || isNotEmpty(value)) && isValidEmail(value),
    },
    {
      showFieldKey: 'form_phone_checked',
      fieldPositionKey: 'form_phone_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'phone',
      fieldLabelKey: 'form_phone_label',
      fieldLabelValue: t('register.form.phone'),
      isFieldRequiredKey: 'form_phone_required',
      fieldErrorMsgKey: 'phone',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_paypal_email_checked',
      fieldPositionKey: 'form_paypal_email_position',
      fieldType: FormFieldType.EMAIL,
      fieldName: 'paypal_email',
      fieldLabelKey: 'form_paypal_email_label',
      fieldLabelValue: t('register.form.paypalEmail'),
      isFieldRequiredKey: 'form_paypal_email_required',
      fieldErrorMsgKey: 'paypal_email',
      isEmail: true,
      validator: (value: any, isRequired: boolean) => (!isRequired || isNotEmpty(value)) && isValidEmail(value),
      hasLiveValidation: true,
    },
    {
      showFieldKey: 'form_company_checked',
      fieldPositionKey: 'form_company_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'company_name',
      fieldLabelKey: 'form_company_label',
      fieldLabelValue: t('register.form.company'),
      isFieldRequiredKey: 'form_company_required',
      fieldErrorMsgKey: 'company',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_street_checked',
      fieldPositionKey: 'form_street_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'street',
      fieldLabelKey: 'form_street_label',
      fieldLabelValue: t('register.form.street'),
      isFieldRequiredKey: 'form_street_required',
      fieldErrorMsgKey: 'street',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_state_checked',
      fieldPositionKey: 'form_state_position',
      fieldType: FormFieldType.CHOICE,
      fieldName: 'state',
      fieldLabelKey: 'form_state_label',
      fieldLabelValue: t('register.form.state'),
      isFieldRequiredKey: 'form_state_required',
      fieldErrorMsgKey: 'state',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_country_checked',
      fieldPositionKey: 'form_country_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'country',
      fieldLabelKey: 'form_country_label',
      fieldLabelValue: t('register.form.country'),
      isFieldRequiredKey: 'form_country_required',
      fieldErrorMsgKey: 'country',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_zip_checked',
      fieldPositionKey: 'form_zip_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'zip',
      fieldLabelKey: 'form_zip_label',
      fieldLabelValue: t('register.form.zip'),
      isFieldRequiredKey: 'form_zip_required',
      fieldErrorMsgKey: 'zip',
      validator: (value: any, isRequired: boolean) => (!isRequired || isNotEmpty(value)) && isValidZipCode(value),
    },
    {
      showFieldKey: 'form_city_checked',
      fieldPositionKey: 'form_city_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'city',
      fieldLabelKey: 'form_city_label',
      fieldLabelValue: t('register.form.city'),
      isFieldRequiredKey: 'form_city_required',
      fieldErrorMsgKey: 'city',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_groups_checked',
      fieldPositionKey: 'form_groups_position',
      fieldType: FormFieldType.CHOICE,
      fieldName: 'groups',
      fieldLabelKey: 'form_groups_label',
      fieldLabelValue: t('register.form.group'),
      isFieldRequiredKey: 'form_groups_required',
      fieldErrorMsgKey: 'groups',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_job_title_checked',
      fieldPositionKey: 'form_job_title_position',
      fieldType: FormFieldType.INPUT,
      fieldName: 'job_title',
      fieldLabelKey: 'form_job_title_label',
      fieldLabelValue: t('register.form.jobTitle'),
      isFieldRequiredKey: 'form_job_title_required',
      fieldErrorMsgKey: 'job_title',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
    {
      showFieldKey: 'form_date_of_birth_checked',
      fieldPositionKey: 'form_date_of_birth_position',
      fieldType: FormFieldType.DATE,
      fieldName: 'date_of_birth',
      fieldLabelKey: 'form_date_of_birth_label',
      fieldLabelValue: t('register.form.dateOfBirth'),
      isFieldRequiredKey: 'form_date_of_birth_required',
      fieldErrorMsgKey: 'date_of_birth',
      validator: (value: any, isRequired: boolean) => !isRequired || isNotEmpty(value),
    },
  ] as FormField[];
}
